import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  MapPin,
  Home,
  Heart,
  Users,
  CalendarDays,
  Clock,
  DollarSign,
  Navigation,
  ExternalLink,
  Mail,
  Share2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Search,
  X,
  Filter,
  Bookmark,
  BookmarkPlus,
  EyeOff,
  ArrowUpDown,
  CalendarRange,
  LayoutGrid,
  Table as TableIcon,
  Calendar as CalendarIcon,
  CalendarPlus,
  Settings,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { EVENT_TYPES, formatDate, formatCurrency, cn } from '../../lib/utils';
import { toast } from 'sonner';

const FREQ_LABELS = {
  every: 'Weekly',
  every_2nd: 'Every 2nd week',
  every_3rd: 'Every 3rd week',
  '1st_and_3rd': '1st & 3rd weeks',
  '2nd_and_4th': '2nd & 4th weeks',
};

const priorityBorder = (event, isFavorited, appStatus) => {
  if (appStatus?.status === 'accepted') return 'border-l-emerald-600';
  if (['draft', 'pending', 'submitted'].includes(appStatus?.status)) return 'border-l-purple-600';
  if (appStatus?.status === 'waitlisted') return 'border-l-orange-500';
  if (appStatus?.status === 'rejected') return 'border-l-rose-500';
  if (isFavorited) return 'border-l-red-500';
  if (event.is_user_event) return 'border-l-gray-500';
  return 'border-l-cyan-500';
};

const datesLine = (event) => {
  const start = formatDate(event.date);
  const end =
    event.end_date && event.end_date !== event.date ? ` – ${formatDate(event.end_date)}` : '';
  const freq = event.recurring_schedule?.frequency
    ? ` · ${FREQ_LABELS[event.recurring_schedule.frequency] || 'Recurring'}`
    : '';
  return `${start}${end}${freq}`;
};

const timeLabel = (event) => {
  const fmt = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return null;
    const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const hour = parseInt(m[1], 10);
    if (isNaN(hour) || hour < 0 || hour > 23) return null;
    const period = hour >= 12 ? 'PM' : 'AM';
    return `${((hour + 11) % 12) + 1}:${m[2]} ${period}`;
  };
  const s = fmt(event.start_time);
  const e = fmt(event.end_time);
  if (s && e) return `${s} – ${e}`;
  if (s) return `From ${s}`;
  if (e) return `Until ${e}`;
  return event.event_hours || event.time || 'Times TBD';
};

const toGoogleCalendarUrl = (event) => {
  const toStamp = (d) => (d || '').replace(/-/g, '');
  const start = toStamp(event.date);
  const end = toStamp(event.end_date || event.date);
  const title = encodeURIComponent(event.title || 'Event');
  const details = encodeURIComponent(
    (event.description || '') + (event.website_url ? `\n\nWebsite: ${event.website_url}` : '')
  );
  const location = encodeURIComponent(
    [event.location, event.city, event.state, event.zip].filter(Boolean).join(', ')
  );
  let endPlus = end;
  if (end && end.length === 8) {
    const nd = new Date(
      parseInt(end.slice(0, 4)),
      parseInt(end.slice(4, 6)) - 1,
      parseInt(end.slice(6, 8)) + 1
    );
    endPlus =
      String(nd.getFullYear()) +
      String(nd.getMonth() + 1).padStart(2, '0') +
      String(nd.getDate()).padStart(2, '0');
  }
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${endPlus}&details=${details}&location=${location}`;
};

const handleShare = async (event) => {
  const url = `${window.location.origin}/events/${event.id}`;
  try {
    if (navigator.share) {
      await navigator.share({
        title: event.title || 'Event on AMSCompass',
        text: `Check out ${event.title} in ${event.city}, ${event.state}`,
        url,
      });
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Event link copied to clipboard');
    }
  } catch (err) {
    if (err.name !== 'AbortError') {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Event link copied to clipboard');
      } catch {
        toast.error('Unable to share');
      }
    }
  }
};

// ----- Compact expandable event item -----
const PanelEventItem = ({ event, isFavorited, appStatus, expanded, onToggle, isHovered, onHover }) => {
  const type = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
  const border = priorityBorder(event, isFavorited, appStatus);
  const address = [event.location, [event.city, event.state].filter(Boolean).join(', '), event.zip]
    .filter(Boolean)
    .join(', ');
  const applyUrl = event.signup_url || event.website_url;
  const emailUrl = event.contact_email
    ? `mailto:${event.contact_email}?subject=${encodeURIComponent(`Inquiry about ${event.title}`)}`
    : null;
  const feeLabel = event.booth_cost != null ? formatCurrency(event.booth_cost) : 'Fee TBD';
  const distanceLabel =
    event.distance != null && !isNaN(event.distance) ? `${event.distance} mi` : '—';

  return (
    <div
      onMouseEnter={() => onHover?.(event.id)}
      onMouseLeave={() => onHover?.(null)}
      className={`bg-white rounded-xl border border-gray-200 border-l-4 ${border} overflow-hidden transition-all ${
        isHovered ? 'shadow-lg ring-2 ring-[#1E3A5F]/20' : 'hover:shadow-md'
      }`}
      data-testid={`map-event-item-${event.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-3 flex items-start gap-2"
        data-testid={`map-event-expand-${event.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: type.color_hex }}
            />
            <h3 className="font-bold text-[#1E3A5F] font-['Outfit'] text-sm leading-snug truncate">
              {event.title}
            </h3>
            {isFavorited && <Heart className="w-3 h-3 text-[#FE5F55] fill-current flex-shrink-0" />}
          </div>
          <p className="text-xs text-[#71717A] mt-0.5 line-clamp-2">{address || 'Location TBD'}</p>
          <p className="text-xs text-[#71717A] mt-0.5">{datesLine(event)}</p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-[#71717A] flex-shrink-0 mt-1" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#71717A] flex-shrink-0 mt-1" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {(event.image_url || event.thumbnail_url) && (
            <div className="h-28 bg-gray-100 overflow-hidden">
              <img
                src={event.image_url || event.thumbnail_url}
                alt={event.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.parentElement.style.display = 'none';
                }}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-1.5 px-3 pt-3">
            {[
              { icon: CalendarDays, label: 'Date(s)', value: datesLine(event) },
              { icon: Clock, label: 'Time', value: timeLabel(event) },
              { icon: DollarSign, label: 'Booth Fee', value: feeLabel },
              { icon: Navigation, label: 'Distance', value: distanceLabel },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-[#F8FAFC] rounded-lg px-2 py-1.5 border border-gray-100">
                <div className="flex items-center gap-1 text-[10px] font-semibold text-[#71717A] uppercase tracking-wide">
                  <Icon className="w-3 h-3" />
                  {label}
                </div>
                <div className="text-xs font-semibold text-[#1E3A5F] mt-0.5 truncate" title={value}>
                  {value}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5 p-3 pb-2">
            <Button
              onClick={() =>
                applyUrl
                  ? window.open(applyUrl, '_blank', 'noopener,noreferrer')
                  : toast.info('No external application link available')
              }
              disabled={!applyUrl}
              size="sm"
              className="bg-[#1E3A5F] hover:bg-[#152a47] text-white h-8 text-xs"
              data-testid={`map-apply-${event.id}`}
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Apply
            </Button>
            <Button
              onClick={() =>
                emailUrl
                  ? (window.location.href = emailUrl)
                  : toast.info('No organizer email available')
              }
              disabled={!emailUrl}
              variant="outline"
              size="sm"
              className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white h-8 text-xs"
              data-testid={`map-contact-${event.id}`}
            >
              <Mail className="w-3 h-3 mr-1" />
              Contact
            </Button>
            <Button
              onClick={() => window.open(toGoogleCalendarUrl(event), '_blank', 'noopener,noreferrer')}
              variant="outline"
              size="sm"
              className="border-gray-300 text-[#4A4A4A] hover:bg-gray-50 h-8 text-xs"
              data-testid={`map-calendar-${event.id}`}
            >
              <CalendarPlus className="w-3 h-3 mr-1" />
              Calendar
            </Button>
            <Button
              onClick={() => handleShare(event)}
              variant="outline"
              size="sm"
              className="border-gray-300 text-[#4A4A4A] hover:bg-gray-50 h-8 text-xs"
              data-testid={`map-share-${event.id}`}
            >
              <Share2 className="w-3 h-3 mr-1" />
              Share
            </Button>
          </div>
          <Link
            to={`/events/${event.id}`}
            className="block text-center text-xs font-semibold text-[#1E3A5F] hover:underline pb-3"
            data-testid={`map-view-details-${event.id}`}
          >
            View Full Details →
          </Link>
        </div>
      )}
    </div>
  );
};

const VIEW_BUTTONS = [
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'year', label: 'Year', icon: CalendarRange },
  { id: 'card', label: 'Cards', icon: LayoutGrid },
  { id: 'table', label: 'Table', icon: TableIcon },
  { id: 'map', label: 'Map', icon: MapPin },
];

// ----- Main side panel -----
export const MapSidePanel = ({
  user,
  visibleEvents,
  searchQuery,
  setSearchQuery,
  hoveredEventId,
  setHoveredEventId,
  onCollapse,
  favoriteIds,
  applicationStatusMap,
  panelProps,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const p = panelProps;

  return (
    <div
      className="absolute md:relative z-[650] inset-y-0 left-0 w-[88vw] max-w-[360px] md:w-[360px] flex-shrink-0 bg-[#FDF9F4] border-r border-gray-200 flex flex-col h-full shadow-xl md:shadow-none"
      data-testid="map-side-panel"
    >
      {/* Header: count + collapse */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <p className="font-bold text-[#1E3A5F] font-['Outfit']" data-testid="map-panel-count">
          {visibleEvents.length} event{visibleEvents.length !== 1 ? 's' : ''} in view
        </p>
        <button
          onClick={onCollapse}
          className="p-1.5 rounded-lg text-[#71717A] hover:bg-gray-100 hover:text-[#1E3A5F]"
          data-testid="map-panel-collapse"
          title="Collapse panel"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-3 space-y-3">
          {/* View switcher */}
          <div className="flex items-center gap-1 p-1 bg-[#FFEDDF] rounded-xl border border-[#1E3A5F]/30">
            {VIEW_BUTTONS.map((btn) => (
              <button
                key={btn.id}
                onClick={() => p.setViewMode(btn.id)}
                className={`flex-1 flex items-center justify-center gap-1 px-1 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                  btn.id === 'map'
                    ? 'bg-[#1E3A5F] text-white'
                    : 'text-[#1E3A5F] hover:bg-[#1E3A5F]/10'
                }`}
                data-testid={`map-panel-view-${btn.id}`}
                title={btn.label}
              >
                <btn.icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
            <Input
              type="text"
              placeholder="Search events in view..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 h-9 bg-white border-gray-200 text-sm rounded-full"
              data-testid="map-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#1E3A5F]"
                data-testid="map-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => p.setEventTab('saved')}
              className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-full text-xs font-semibold transition-colors ${
                p.eventTab === 'saved'
                  ? 'bg-[#1E3A5F] text-white'
                  : 'bg-white text-[#1E3A5F] border border-[#1E3A5F]/40 hover:bg-[#FFEDDF]'
              }`}
              data-testid="map-tab-saved"
            >
              <Heart className="w-3.5 h-3.5" />
              Saved ({p.savedCount})
            </button>
            <button
              onClick={() => p.setEventTab('all')}
              className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-full text-xs font-semibold transition-colors ${
                p.eventTab === 'all'
                  ? 'bg-[#1E3A5F] text-white'
                  : 'bg-white text-[#1E3A5F] border border-[#1E3A5F]/40 hover:bg-[#FFEDDF]'
              }`}
              data-testid="map-tab-all"
            >
              <CalendarDays className="w-3.5 h-3.5" />
              {p.allTabLabel} ({p.allCount})
            </button>
            {p.viewCommunityEnabled ? (
              <button
                onClick={() => p.setEventTab('community')}
                className={`col-span-2 flex items-center justify-center gap-1.5 px-2 py-2 rounded-full text-xs font-semibold transition-colors ${
                  p.eventTab === 'community'
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-[#FFEDDF] text-[#1E3A5F] border border-[#1E3A5F]/40 hover:bg-[#1E3A5F]/10'
                }`}
                data-testid="map-tab-community"
              >
                <Users className="w-3.5 h-3.5" />
                Community Events ({p.communityCount})
              </button>
            ) : (
              <Link
                to="/settings"
                className="col-span-2 text-center text-xs text-[#1E3A5F] hover:underline py-1"
              >
                <Users className="w-3.5 h-3.5 inline mr-1" />
                Enable Community Events in Settings
              </Link>
            )}
          </div>

          {/* Home location */}
          <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-3 py-2">
            <div className="w-7 h-7 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
              <Home className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[#71717A] uppercase tracking-wide font-semibold">
                Home Location
              </p>
              <p className="text-xs font-semibold text-[#1E3A5F] truncate">
                {user?.home_state
                  ? [user.home_city, user.home_state, user.home_zip].filter(Boolean).join(', ')
                  : 'Not set'}
              </p>
            </div>
            <Link to="/settings" data-testid="map-home-edit">
              <Settings className="w-4 h-4 text-[#71717A] hover:text-[#1E3A5F]" />
            </Link>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5"
              data-testid="map-filters-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-[#1E3A5F]">
                <Filter className="w-4 h-4" />
                Filters
                {p.hasActiveFilters && (
                  <span className="px-2 py-0.5 bg-[#FE5F55] text-white text-[10px] rounded-full">
                    Active
                  </span>
                )}
              </span>
              {filtersOpen ? (
                <ChevronUp className="w-4 h-4 text-[#71717A]" />
              ) : (
                <ChevronDown className="w-4 h-4 text-[#71717A]" />
              )}
            </button>

            {filtersOpen && (
              <div className="px-3 pb-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    value={p.filterEventType || 'all_types'}
                    onValueChange={(v) => p.setFilterEventType(v === 'all_types' ? '' : v)}
                  >
                    <SelectTrigger className="h-9 text-xs rounded-full bg-white" data-testid="map-filter-event-type">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_types">All Types</SelectItem>
                      {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={p.filterVenueType || 'any_venue'}
                    onValueChange={(v) => p.setFilterVenueType(v === 'any_venue' ? '' : v)}
                  >
                    <SelectTrigger className="h-9 text-xs rounded-full bg-white" data-testid="map-filter-venue-type">
                      <SelectValue placeholder="All Venues" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any_venue">All Venues</SelectItem>
                      <SelectItem value="indoor">Indoor</SelectItem>
                      <SelectItem value="outdoor">Outdoor</SelectItem>
                    </SelectContent>
                  </Select>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'h-9 text-xs rounded-full justify-start font-normal',
                          !p.filterDateFrom && 'text-gray-400'
                        )}
                        data-testid="map-filter-date-from"
                      >
                        <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                        {p.filterDateFrom ? format(p.filterDateFrom, 'MMM d, yy') : 'From Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={p.filterDateFrom} onSelect={p.setFilterDateFrom} initialFocus />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'h-9 text-xs rounded-full justify-start font-normal',
                          !p.filterDateTo && 'text-gray-400'
                        )}
                        data-testid="map-filter-date-to"
                      >
                        <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                        {p.filterDateTo ? format(p.filterDateTo, 'MMM d, yy') : 'To Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={p.filterDateTo} onSelect={p.setFilterDateTo} initialFocus />
                    </PopoverContent>
                  </Popover>

                  <Input
                    type="number"
                    placeholder="Max Distance (mi)"
                    value={p.filterMaxDistance}
                    onChange={(e) => p.setFilterMaxDistance(e.target.value)}
                    className="h-9 text-xs rounded-full"
                    data-testid="map-filter-max-distance"
                  />
                  <Input
                    placeholder="Keywords"
                    value={p.filterKeywords}
                    onChange={(e) => p.setFilterKeywords(e.target.value)}
                    className="h-9 text-xs rounded-full"
                    data-testid="map-filter-keywords"
                  />
                  <Input
                    type="number"
                    placeholder="Min Fee ($)"
                    value={p.filterMinCost}
                    onChange={(e) => p.setFilterMinCost(e.target.value)}
                    className="h-9 text-xs rounded-full"
                    data-testid="map-filter-min-cost"
                  />
                  <Input
                    type="number"
                    placeholder="Max Fee ($)"
                    value={p.filterMaxCost}
                    onChange={(e) => p.setFilterMaxCost(e.target.value)}
                    className="h-9 text-xs rounded-full"
                    data-testid="map-filter-max-cost"
                  />
                </div>

                {/* Sort */}
                <Select value={p.sortBy} onValueChange={p.setSortBy}>
                  <SelectTrigger className="h-9 text-xs rounded-full bg-white" data-testid="map-sort-dropdown">
                    <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="priority">Priority (Apps First)</SelectItem>
                    <SelectItem value="date">Date (Earliest)</SelectItem>
                    <SelectItem value="distance">Distance (Closest)</SelectItem>
                    <SelectItem value="title">Alphabetical</SelectItem>
                    <SelectItem value="deadline">Deadline (Earliest)</SelectItem>
                  </SelectContent>
                </Select>

                {/* Hide Expired */}
                <div className="flex items-center justify-between px-1 py-1">
                  <Label
                    htmlFor="map-hide-expired"
                    className="text-xs font-medium text-[#1E3A5F] cursor-pointer flex items-center gap-1.5"
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                    Hide Expired
                  </Label>
                  <Switch
                    id="map-hide-expired"
                    checked={p.hideExpired}
                    onCheckedChange={p.setHideExpired}
                    data-testid="map-hide-expired-toggle"
                  />
                </div>

                {/* Saved Filters */}
                {p.savedFilters.length > 0 && (
                  <div className="pt-1 border-t border-gray-100">
                    <p className="text-[10px] font-semibold text-[#71717A] uppercase tracking-wide flex items-center gap-1 mb-1.5">
                      <Bookmark className="w-3 h-3" />
                      Saved Filters
                    </p>
                    <div className="space-y-1">
                      {p.savedFilters.map((filter) => (
                        <div
                          key={filter.id}
                          className="flex items-center justify-between px-2 py-1 hover:bg-[#FFEDDF] rounded-lg"
                        >
                          <button
                            onClick={() => p.loadFilter(filter)}
                            className="flex-1 text-left text-xs text-[#1E3A5F] font-medium truncate"
                            data-testid={`map-load-filter-${filter.id}`}
                          >
                            {filter.name}
                          </button>
                          <button
                            onClick={() => p.deleteFilter(filter.id)}
                            className="p-0.5 text-gray-400 hover:text-[#FE5F55]"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {p.hasActiveFilters && (
                  <div className="flex items-center justify-end gap-3 pt-1">
                    <button
                      onClick={p.openSaveFilterDialog}
                      className="flex items-center gap-1 text-xs text-[#1E3A5F] hover:underline"
                      data-testid="map-save-filter-btn"
                    >
                      <BookmarkPlus className="w-3.5 h-3.5" />
                      Save Filter
                    </button>
                    <button
                      onClick={p.clearFilters}
                      className="flex items-center gap-1 text-xs text-[#FE5F55] hover:underline"
                      data-testid="map-clear-filters-btn"
                    >
                      <X className="w-3.5 h-3.5" />
                      Clear
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Event list */}
          <div className="space-y-2" data-testid="map-events-list">
            {visibleEvents.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-gray-300 p-6 text-center">
                <MapPin className="w-8 h-8 text-[#71717A] mx-auto mb-2" />
                <p className="text-sm text-[#4A4A4A] font-semibold mb-1">No events in this view</p>
                <p className="text-xs text-[#71717A]">
                  Try zooming out, panning the map, or adjusting your filters.
                </p>
              </div>
            ) : (
              visibleEvents.map((event) => (
                <PanelEventItem
                  key={event.id}
                  event={event}
                  isFavorited={favoriteIds.has(event.id)}
                  appStatus={applicationStatusMap[event.id]}
                  expanded={expandedId === event.id}
                  onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
                  isHovered={hoveredEventId === event.id}
                  onHover={setHoveredEventId}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapSidePanel;
